import React from "react";
import {
    IProtocolStep,
    IProtocolStepKind,
} from "../../types/protocol";
import {IProtocolStepDefinition} from "../../types/protocolDefinitions";
import {TSubjectId} from "@/common/types";
import {DeepPartial} from "@/utils/types/DeepPartial";

// Import individual step view components
import {
    ScanStepView,
    StimStepView,
    StartDataCollectionStepView,
    StopDataCollectionStepView,
    RestStepView,
    MeasureBpStepView,
    MeasurePlrStepView,
    MeasureHrStepView,
    QaStepView,
    QuestionnaireStepView,
    NoteStepView,
    InstructionStepView,
    VasPainScoreStepView,
    NRSPainScoreStepView,
    PPReliefStepView,
    PortConfigStepView,
} from "./views";
import {GroupStepView} from "@/protocols/common/steps/views/GroupStepView.tsx";

/** Union type for step partial updates */
export type TStepPartialUpdate = DeepPartial<IProtocolStep | IProtocolStepDefinition>;

/** Workflow mode for step views - controls editability and display behavior */
export type TWorkflow = "planning" | "session" | "review" | "review_edit";

/**
 * Primary step view component that renders the appropriate view for each step type.
 *
 * This is the default rendering component for all protocol steps, used in:
 * - Protocol execution/recorder mode (IProtocolStep with timezone) - full functionality
 * - Protocol planning/definition mode (IProtocolStepDefinition without timezone) - time pickers show placeholders
 * - Review mode - all fields disabled (read-only)
 * - Review_edit mode - fields editable like session mode, but with Cancel/Update buttons at StepRow level
 * - Group steps and standalone steps alike
 *
 * When timezone is undefined (planning mode), time-related fields display placeholder text
 * indicating values will be set during protocol execution.
 *
 * Each step type has its own dedicated view component in the ./views directory.
 */
export const StepView: React.FC<{
    step: IProtocolStep | IProtocolStepDefinition;
    subjectId?: TSubjectId;
    timezone?: string;
    onChange: (updates: TStepPartialUpdate) => void;
    workflow?: TWorkflow;
    focusMode?: boolean;
}> = ({step, subjectId, timezone, onChange, workflow = "session", focusMode = false}) => {
    switch (step.kind) {
        case IProtocolStepKind.MEG_SCAN:
        case IProtocolStepKind.EEG_SCAN:
        case IProtocolStepKind.ECG_SCAN:
            return (
                <ScanStepView
                    step={step}
                    subjectId={subjectId}
                    onChange={onChange}
                    workflow={workflow}
                />
            );

        case IProtocolStepKind.STIM_EVENT:
            return (
                <StimStepView
                    step={step}
                    onChange={onChange}
                    workflow={workflow}
                />
            );

        case IProtocolStepKind.START_DATA_COLLECTION:
            return (
                <StartDataCollectionStepView
                    step={step}
                    subjectId={subjectId}
                    onChange={onChange}
                    workflow={workflow}
                />
            );

        case IProtocolStepKind.STOP_DATA_COLLECTION:
            return <StopDataCollectionStepView step={step} subjectId={subjectId} workflow={workflow} />;

        case IProtocolStepKind.REST_EVENT:
            return (
                <RestStepView
                    step={step}
                    onChange={onChange}
                    workflow={workflow}
                />
            );

        case IProtocolStepKind.MEASURE_BP:
            return (
                <MeasureBpStepView
                    step={step}
                    timezone={timezone}
                    onChange={onChange}
                    workflow={workflow}
                />
            );

        case IProtocolStepKind.MEASURE_PLR:
            return (
                <MeasurePlrStepView
                    step={step}
                    timezone={timezone}
                    onChange={onChange}
                    workflow={workflow}
                />
            );

        case IProtocolStepKind.MEASURE_HR:
            return (
                <MeasureHrStepView
                    step={step}
                    onChange={onChange}
                    workflow={workflow}
                />
            );

        case IProtocolStepKind.Q_AND_A:
            return (
                <QaStepView
                    step={step}
                    onChange={onChange}
                    workflow={workflow}
                />
            );

        case IProtocolStepKind.QUESTIONNAIRE:
            return (
                <QuestionnaireStepView
                    step={step}
                    onChange={onChange}
                    workflow={workflow}
                    focusMode={focusMode}
                />
            );

        case IProtocolStepKind.NOTE:
            return <NoteStepView step={step} onChange={onChange} workflow={workflow} />;

        case IProtocolStepKind.GROUP:
            // Groups contain other steps and are rendered differently
            return <GroupStepView step={step} onChange={onChange} workflow={workflow}/>;

        case IProtocolStepKind.INSTRUCTIONS:
            return <InstructionStepView step={step} onChange={onChange} workflow={workflow} />;

        case IProtocolStepKind.VAS_PAIN_SCORE:
            return <VasPainScoreStepView step={step} timezone={timezone} onChange={onChange} workflow={workflow} />;

        case IProtocolStepKind.NRS_PAIN_SCORE:
            return <NRSPainScoreStepView step={step} timezone={timezone} onChange={onChange} workflow={workflow} />;

        case IProtocolStepKind.PP_RELIEF:
            return <PPReliefStepView step={step} timezone={timezone} onChange={onChange} workflow={workflow} />;

        case IProtocolStepKind.PORT_CONFIG:
            return <PortConfigStepView step={step} onChange={onChange} workflow={workflow} />;

        default:
            return null;
    }
};

